import {
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Typography,
} from '@mui/material'
import { useCallback, useState } from 'react'
import { Button, InformationModal, PointDisplay } from 'utoniq-ui'

import {
  GachaTicketConnectionFragment,
  Maybe,
  TicketChargeQuery,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { BrowserLink } from '@/components/lib/BrowserLink'
import { Display, HeaderDisplay } from '@/components/organisms'
import { useMessage, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Organism, Template } from '@/core/architecture'
import { getHelpUrl } from '@/lib/helpUrl'

export * from './fragments'

export type State = {
  ticket: Maybe<number>
  gachaTicketByPointPurchasing: boolean
  gachaTicketByPointInBulkPurchasing: boolean
  purchasableGachaTickets: Maybe<TicketChargeQuery['purchasableGachaTickets']>
  gachaTicket: Maybe<GachaTicketConnectionFragment>
}

type TicketDisplayCellProps = {
  ticket: State['ticket']
  gachaTicket: State['gachaTicket']
}

type TicketChargeButtonCellProps = {
  ticket: string
  point: string
  price: string
  onClick: () => void
  gachaTicket: State['gachaTicket']
}

type TextLinkCellProps = {}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onPurchaseGachaTicket: (count: number, prepaidPoint: number) => Promise<void>
}

export const TicketChargeTemplate: Template<State, Action> = ({
  state,
  onPurchaseGachaTicket,
}) => {
  const { t } = useTranslation('ticketCharge')
  const { routerNames } = useRouter()
  const [openModalConfirm, setOpenModalConfirm] = useState<boolean>(false)
  const [openModalComplete, setOpenModalComplete] = useState<boolean>(false)
  const [openModalError, setOpenModalError] = useState<boolean>(false)
  const [plan, setPlan] =
    useState<Maybe<{ count: number; prepaidPoint: number; price: string }>>(
      null
    )
  const { showError } = useMessage()

  const handleClickTicketCharge = useCallback(
    async (count: number, prepaidPoint: number, price: string) => {
      setPlan({ count, prepaidPoint, price })
      setOpenModalConfirm(true)
    },
    []
  )

  const handlePurchaseTicket = useCallback(async () => {
    if (!plan) {
      setOpenModalConfirm(false)
      return
    }
    onPurchaseGachaTicket(plan.count, plan.prepaidPoint)
      .then(() => {
        setOpenModalConfirm(false)
        setOpenModalComplete(true)
      })
      .catch(e => {
        setOpenModalConfirm(false)
        if (e.message === 'Insufficient Prepaid Point balance.') {
          return setOpenModalError(true)
        }
        if (e instanceof Error) showError(e)
      })
  }, [plan, onPurchaseGachaTicket, showError])

  return (
    <BaseLayout margin header={{ back: true, title: t('title') }}>
      <Head title={routerNames.GachaTicketPurchase} />
      <TicketDisplayCell
        ticket={state.ticket}
        gachaTicket={state.gachaTicket}
      />
      {state.purchasableGachaTickets?.map(
        (gachaTicket, index) =>
          gachaTicket && (
            <TicketChargeButtonCell
              key={index}
              ticket={gachaTicket.displayCount}
              price={gachaTicket.formattedPrice || '0'}
              point={gachaTicket.formattedPrepaidPoint || '0'}
              gachaTicket={state.gachaTicket}
              onClick={() =>
                handleClickTicketCharge(
                  gachaTicket.count,
                  gachaTicket.prepaidPoints ?? 0,
                  gachaTicket.formattedPrice || '0'
                )
              }
            />
          )
      )}
      <TextLinkCell />
      <ConfirmTicketChargeModal
        loading={
          state.gachaTicketByPointPurchasing ||
          state.gachaTicketByPointInBulkPurchasing
        }
        open={openModalConfirm}
        onClose={() => setOpenModalConfirm(false)}
        count={plan?.count ?? 0}
        price={plan?.price ?? 0}
        onConfirm={handlePurchaseTicket}
      />
      <CompleteTicketChargeModal
        open={openModalComplete}
        onClose={() => setOpenModalComplete(false)}
      />
      <ErrorTicketChargeModal
        open={openModalError}
        onClose={() => setOpenModalError(false)}
      />
    </BaseLayout>
  )
}

export const TicketDisplayCell: Cell<TicketDisplayCellProps> = ({
  ticket,
  gachaTicket,
}) => {
  const { t } = useTranslation('ticketCharge')
  return (
    <CellFrame sx={{ py: 2 }} alignContent="center">
      <Typography
        variant="h6"
        color="text.secondary"
        align="center"
        sx={{ pb: 1 }}
      >
        {t('gachaTicket')}
      </Typography>
      <Display
        ticket={ticket ?? ''}
        src={gachaTicket?.thumbnails?.standard?.smallUri || ''}
        variant="h1"
      />
    </CellFrame>
  )
}

export const TicketChargeButtonCell: Cell<TicketChargeButtonCellProps> = ({
  ticket,
  point,
  onClick,
  gachaTicket,
}) => (
  <CellFrame sx={{ pt: 4 }}>
    <Card sx={{ borderRadius: 2 }}>
      <CardActionArea onClick={onClick}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ height: 56, mr: 3, ml: 5 }}
        >
          <Stack direction="row" alignItems="center">
            <HeaderDisplay
              ticket={ticket}
              src={gachaTicket?.thumbnails?.standard?.smallUri || ''}
              sx={{ bgcolor: 'transparent' }}
            />
            {/*
              @description
              「○○枚お得！」の表記はdescriptionのAPIができるまで非表示にしておく。
              https://app.clickup.com/t/908127/UT-3972

              <EmphasizedNumber variant="overline" color="primary.main" sx={{ ml: 2 }}>
                {description}
              </EmphasizedNumber>
              */}
          </Stack>
          <PointDisplay point={point} />
        </Stack>
      </CardActionArea>
    </Card>
  </CellFrame>
)

const TextLinkCell: Cell<TextLinkCellProps> = () => {
  const { t } = useTranslation('ticketCharge')
  return (
    <CellFrame sx={{ py: 4 }}>
      <Card elevation={0}>
        <CardContent sx={{ p: 4, '&:last-child': { pb: 4 } }}>
          <Stack spacing={2} alignItems="center" justifyContent="center">
            <Typography variant="caption" color="text.secondary">
              {t('cancelPurchaseNote')}
            </Typography>
            <Stack direction="row" spacing={5}>
              <BrowserLink
                variant="subtitle1"
                href={getHelpUrl('wageSettlementMethod')}
              >
                {t('wageSettlementMethod')}
              </BrowserLink>
              <BrowserLink
                variant="subtitle1"
                href={getHelpUrl('specifiedCommercialTransactionLaw')}
              >
                {t('specifiedCommercialTransactionLaw')}
              </BrowserLink>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </CellFrame>
  )
}

type ConfirmPointChargeModalProps = {
  loading: boolean
  open: boolean
  onClose: () => void
  count?: number
  price?: number | string
  onConfirm: () => Promise<void>
}
const ConfirmTicketChargeModal: Organism<ConfirmPointChargeModalProps> = ({
  loading,
  open,
  count,
  price,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation('ticketCharge')
  return (
    <InformationModal
      open={open}
      onClose={onClose}
      title={t('purchaseConfirmTitle')}
      content={t('purchaseConfirmDesc', { count, price })}
      cancelText={t('cancel')}
      buttons={
        <Button
          sx={{ minWidth: 212 }}
          variant="contained"
          color="primary"
          size="large"
          onClick={onConfirm}
          loading={loading}
        >
          {t('toBuy')}
        </Button>
      }
    />
  )
}

type CompleteTicketChargeModalProps = {
  open: boolean
  onClose: () => void
}
const CompleteTicketChargeModal: Organism<CompleteTicketChargeModalProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation('ticketCharge')
  return (
    <InformationModal
      open={open}
      onClose={onClose}
      title={t('purchaseCompleteTitle')}
      content={t('purchaseCompleteDesc')}
      buttons={
        <Button
          sx={{ minWidth: 212 }}
          variant="contained"
          color="primary"
          size="large"
          onClick={onClose}
        >
          {t('ok')}
        </Button>
      }
    />
  )
}

type ErrorTicketChargeModalProps = {
  open: boolean
  onClose: () => void
}
const ErrorTicketChargeModal: Organism<ErrorTicketChargeModalProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation('ticketCharge')
  const { routerPages } = useRouter()
  return (
    <InformationModal
      open={open}
      onClose={onClose}
      title={t('purchaseErrorTitle')}
      content={t('purchaseErrorDesc')}
      cancelText={t('cancel')}
      buttons={
        <Button
          sx={{ minWidth: 212 }}
          variant="contained"
          color="primary"
          size="large"
          href={routerPages.PrepaidPointCharge}
        >
          {t('buyPoints')}
        </Button>
      }
    />
  )
}
